import React, { useContext, useEffect, useReducer } from "react"
import { useHistory } from "react-router-dom"
import {
  getAuth,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  updatePassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  deleteUser
} from "@firebase/auth"
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  serverTimestamp
} from "@firebase/firestore"
import { message } from "antd"
import { showLoader } from "../utils/loader"
import { db, firebaseConfig } from "../firebase"
import { getParameterByName } from "../utils/browser"

const usersRef = "users"
const auth = getAuth()

const AuthContext = React.createContext()

const initialState = {
  status: "loading",
  user: {
    uid: null,
    displayName: "Loading...",
    limit: 20
  },
  logged: false,
  claims: []
}

function reducer(state, action) {
  switch (action.type) {
    case "login": {
      return {
        ...state,
        user: {
          ...action.profile,
          uid: action.uid,
        },
        status: action.status,
        logged: true
      }
    }
    case "updateProfile": {
      return {
        ...state,
        user: {
          ...state.user, ...action.updated
        },
        status: action.status,
      }
    }
    case "updateField": {
      return {
        ...state,
        user: {
          ...state.user, ...action.field
        },
        status: action.status,
      }
    }
    case "authError": {
      return {
        ...state,
        status: action.status
      }
    }
    case "claims": {
      return {
        ...state,
        claims: action.claims
      }
    }
    case "logout": {
      return {
        ...initialState,
        status: "idle"
      }
    }
    default:
      return state
  }
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const history = useHistory()
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        let user = currentUser
        const profile = await getDoc(doc(collection(db, usersRef), user.uid)).then(doc => doc.data())

        dispatch({
          type: "login",
          uid: user.uid,
          profile: profile,
          status: "idle"
        })

        currentUser.getIdTokenResult()
          .then((idTokenResult) => {
            showLoader(false)
            dispatch({
              type: "claims",
              // claims: { ...idTokenResult.claims, role: "admin" },
              claims: { ...idTokenResult.claims, role: profile.role },
            })

            // setClaims(idTokenResult.claims)
            // console.log("claims", idTokenResult.claims)
            // // Confirm the user is an Admin.
            // if (!!idTokenResult.claims.admin) {
            //   console.log("admin")
            //   // Show admin UI.
            //   // showAdminUI()
            // } else {
            //   // Show regular user UI.
            //   // showRegularUI()
            // }
          })
          .catch((error) => {
            showLoader(false)
            console.log(error)
          })
      } else {
        dispatch({
          type: "authError",
          status: "idle"
        })
        showLoader(false)
      }
    }, error => {
      dispatch({
        type: "authError",
        status: "idle"
      })
      console.log(error)
      showLoader(false)
    })
    return unsubscribe
  }, [])

  /**
   *
   * @param {*} email
   * @param {*} password
   * @returns
   */
  const signup = (email, password, profile) => new Promise((resolve, reject) => {
    showLoader(true)
    message.loading({ content: 'Requesting...', key: "signup" })
    createUserWithEmailAndPassword(auth, email, password)
      .then(async userCredential => {
        // Signed in
        const user = userCredential.user
        profile.createdAt = serverTimestamp()
        setDoc(doc(collection(db, usersRef), user.uid), profile)
        dispatch({
          type: "login",
          uid: user.uid,
          // displayName: user.providerData[0]?.displayName || "User",
          // profile: user.providerData[0],
          profile: profile,
          cars: [],
          status: "idle"
        })
        message.success({ content: "Registered in successfully.", key: "signup" })
        return resolve(user)
      })
      .catch(error => {
        var errorCode = error.code
        var errorMessage = error.message
        if (errorCode === 'auth/email-already-in-use') {
          errorMessage = "This email is already registered. Please try to login with it."
        }
        if (errorCode === 'auth/wrong-password') {
          errorMessage = "Wrong password."
        }
        if (errorCode === 'auth/weak-password') {
          errorMessage = "The password is too weak."
        }
        message.error({ content: errorMessage, key: "signup" })
        reject(error)
      })
      .finally(() => {
        showLoader(false)
      })
  })

  /**
   *
   * @param {*} email
   * @param {*} password
   * @returns Promise<user>
   */
  const login = (email, password) => new Promise((resolve, reject) => {
    showLoader(true)
    message.loading({ content: 'Requesting...', key: "login" })
    signInWithEmailAndPassword(auth, email, password)
      .then(async userCredential => {
        // Signed in
        const user = userCredential.user
        const userProfile = await getDoc(doc(collection(db, usersRef), user.uid)).then(doc => doc.data())

        dispatch({
          type: "login",
          uid: user.uid,
          // displayName: user.providerData[0].displayName,
          profile: userProfile,
          status: "idle"
        })

        // showLoader(false)
        message.success({ content: "Logged in successfully.", key: "login" })
        return resolve(userProfile)
        // return user
      })
      .catch(error => {
        var errorCode = error.code
        var errorMessage = error.message
        if (errorCode === 'auth/wrong-password') {
          errorMessage = "Wrong password."
        }
        if (errorCode === 'auth/user-disabled') {
          errorMessage = "Your account is blocked."
        }
        if (errorCode === 'auth/user-not-found') {
          errorMessage = "Account does not exist."
        }
        message.error({ content: errorMessage, key: "login" })
        reject(error)
      })
      .finally(() => {
        showLoader(false)
      })
  })

  const logout = (redirectTo = "") => {
    showLoader(true)
    message.loading({ content: 'Requesting...', key: "logout" })
    signOut(auth)
      .then(() => {
        dispatch({ type: "logout" })
        message.success({ content: "Logged out successfully.", key: "logout" })
        redirectTo && history.push(redirectTo)
        // return resolve()
      })
      .catch((error) => {
        var errorMessage = error.message
        message.error({ content: errorMessage, key: "logout" })
      })
      .finally(() => {
        showLoader(false)
      })
  }

  const passwordResetEmail = (email) => new Promise((resolve) => {
    showLoader(true)
    message.loading({ content: 'Requesting...', key: "resetPass" })

    sendPasswordResetEmail(auth, email)
      .then(function () {
        // Password Reset Email Sent!
        message.success({ content: 'Password Reset Email Sent!', key: "resetPass" })
        return resolve()
      })
      .catch(function (error) {
        // Handle Errors here.
        var errorCode = error.code
        var errorMessage = error.message
        if (errorCode === 'auth/invalid-email') {
          errorMessage = "Email is not valid."
        } else if (errorCode === 'auth/user-not-found') {
          errorMessage = "User not found with this email."
        }
        message.error({ content: errorMessage, key: "resetPass" })
        console.log(error)
        // return reject(error)
      })
      .finally(() => {
        showLoader(false)
      })
  })

  const verifyPasswordResetCodeHandle = (redirectTo = '') => new Promise((resolve, reject) => {
    let actionCode = getParameterByName('oobCode')
    if (!actionCode) {
      message.error({ content: 'Your link has expired!, please reset password again' })
      redirectTo && history.push(redirectTo)
      return reject()
    } else if (firebaseConfig.apiKey !== getParameterByName('apiKey')) {
      message.error({ content: 'Your link has malformed!, please create it again' })
      redirectTo && history.push(redirectTo)
      return reject()
    }
    verifyPasswordResetCode(auth, actionCode)
      .then(function (email) {
        return resolve(email)
      })
      .catch(function (error) {
        // Handle Errors here.
        var errorCode = error.code
        var errorMessage = error.message
        if (errorCode === "auth/expired-action-code") {
          errorMessage = 'Your link has expired!, please create it again'
        }
        message.error({ content: errorMessage })
        console.log(error)
        return reject(error)
        // history.push(error)
      })
      .finally(() => {
        showLoader(false)
      })
  })

  const confirmPasswordResetHandle = (newPassword, redirectTo = "") => {
    let actionCode = getParameterByName('oobCode')
    if (actionCode) {
      showLoader(true)
      message.loading({ content: 'Updating password...', key: "updatePass" })

      // Save the new password.
      confirmPasswordReset(auth, actionCode, newPassword)
        .then((resp) => {
          // Password reset has been confirmed and new password updated.
          // console.log('resp', resp)
          message.success({ content: 'Youre password updated successfully.', key: "updatePass" })
          let continueUrl = getParameterByName('continueUrl')
          if (continueUrl) {
            // TODO: If a continue URL is available, display a button which on
            // click redirects the user back to the app via continueUrl with
            // additional state determined from that URL's parameters.
            setTimeout(() => {
              history.push(continueUrl)
            }, 300)
          } else {
            redirectTo && history.push(redirectTo)
          }
        })
        .catch((error) => {
          message.error({ content: error.message, key: "updatePass" })
          // Error occurred during confirmation. The code might have expired or the
          // password is too weak.
        })
        .finally(() => {
          showLoader(false)
        })
    } else {
      message.error({ content: 'Your link has expired!, please create it again', key: "updatePass" })
      redirectTo && history.push(redirectTo)
    }
  }

  // function changeEmail(email) {
  //   return updateEmail(user, email)
  // }

  const updatePass = (password, redirectTo = "") => {
    showLoader(true)
    message.loading({ content: 'Updating password...', key: "updatePass" })
    updatePassword(auth.currentUser, password)
      .then(() => {
        // Update successful.
        showLoader(false)
        message.success({ content: 'Your password updated successfully.', key: "updatePass" })
        setTimeout(() => {
          redirectTo && history.push(redirectTo)
        }, 300)
      })
      .catch((error) => {
        // An error ocurred
        console.log(error)
        showLoader(false)
        message.error({ content: error.message, key: "updatePass" })
      })
  }

  const updateAuthProfile = (profile) => {
    updateProfile(auth.currentUser, profile)
      .then(() => {
        console.log("Updated")
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const updateUserProfile = (uid, payload) => new Promise((resolve) => {
    showLoader(true)
    message.loading({ content: 'Updating profile...', key: "updateProfile" })
    updateDoc(doc(db, usersRef, uid), payload)
      .then(() => {
        message.success({ content: 'Profile updated successfully.', key: "updateProfile" })
        dispatch({
          type: "updateProfile",
          updated: payload,
          status: "idle"
        })
        return resolve()
      })
      .catch((error) => {
        var errorMessage = error.message
        message.error({ content: errorMessage, key: "updateProfile" })
      })
      .finally(() => {
        showLoader(false)
      })
  })

  const deleteAuthUser = (uid) => new Promise((resolve) => {
    const user = auth.currentUser
    showLoader(true)
    message.loading({ content: 'Updating profile...', key: "deleteAuthUser" })

    deleteUser(user)
      .then(() => {
        dispatch({ type: "logout" })
        return resolve()
      })
      .catch((error) => {
        var errorMessage = error.message
        message.error({ content: errorMessage, key: "deleteAuthUser" })
      })
      .finally(() => {
        showLoader(false)
      })
  })

  console.log("AuthContext", state)

  return (
    <AuthContext.Provider value={{
      ...state,
      dispatch,
      signup,
      login,
      logout,
      passwordResetEmail,
      updateAuthProfile,
      updateUserProfile,
      updatePass,
      verifyPasswordResetCodeHandle,
      confirmPasswordResetHandle,
      deleteAuthUser
    }}>
      {/* <pre>{JSON.stringify(state, null, 2)}</pre> */}
      {children}
    </AuthContext.Provider>
  )
}
